// Update checking for the sideloaded Android app.
//
// An app installed from the Play Store updates itself. One installed from an
// APK never does — it stays on whatever build was current the day it was
// installed, forever, while the API it talks to keeps moving. For a storefront
// that eventually means someone checking out through months-old checkout code.
//
// So the app asks on every launch and resume. The server compares versionCodes
// (see routes/appDownload.js) rather than the client doing it, so the rule for
// what counts as "out of date" lives in one place and a stale client cannot get
// it wrong.
//
// Two strengths of notice:
//   · updateAvailable — a dismissible bar. Most releases.
//   · mandatory       — a blocking screen, for when the installed build is
//                       below minSupportedVersionCode because it is genuinely
//                       broken against the current API. Set deliberately, by
//                       passing --min-version to `npm run publish:apk`.
import { useState, useEffect, useCallback } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { isNativePlatform } from './nativeBridge';
import { API_BASE_URL } from './config';
import { IconDownload, IconX, IconAlertTriangle } from './Icons';

const SNOOZE_KEY = 'acymart_update_snoozed_for';

export default function AppUpdateNotice() {
  const [update, setUpdate] = useState(null);
  const [snoozedFor, setSnoozedFor] = useState(null);

  useEffect(() => {
    try { setSnoozedFor(window.localStorage.getItem(SNOOZE_KEY)); } catch { /* private mode */ }
  }, []);

  const check = useCallback(async () => {
    if (!isNativePlatform()) return;
    try {
      // build is the Android versionCode (a string from the plugin, hence the
      // Number()). getInfo() is native-only, which the guard above already
      // ensures.
      const info = await CapacitorApp.getInfo();
      const installed = Number(info.build);
      if (!Number.isFinite(installed)) return;

      const res = await fetch(`${API_BASE_URL}/api/app/version?installedVersionCode=${installed}`);
      const data = await res.json();
      setUpdate(data?.available && data.updateAvailable ? data : null);
    } catch {
      // Offline, or no APK published on this server. An update check is not
      // something to interrupt shopping over -- stay quiet and try again on
      // the next resume.
      setUpdate(null);
    }
  }, []);

  useEffect(() => {
    check();
    if (!isNativePlatform()) return undefined;
    // Re-check when the app comes back to the foreground: a phone that is never
    // fully closed would otherwise only ever check once, on the day it was
    // installed.
    let handle;
    CapacitorApp.addListener('appStateChange', ({ isActive }) => { if (isActive) check(); })
      .then((h) => { handle = h; })
      .catch(() => { /* plugin missing on web */ });
    return () => { handle?.remove?.(); };
  }, [check]);

  const startDownload = () => {
    // Opened rather than navigated to. The Capacitor WebView has no download
    // manager, so loading the APK inside it does nothing visible; the API host
    // is a different origin from the app's https://localhost, which makes
    // Capacitor hand the URL to the system browser, where Chrome's real
    // download manager takes it.
    const url = `${API_BASE_URL}/api/app/download`;
    try { window.open(url, '_blank'); } catch { window.location.href = url; }
  };

  const snooze = () => {
    setSnoozedFor(String(update.versionCode));
    try { window.localStorage.setItem(SNOOZE_KEY, String(update.versionCode)); } catch { /* private mode */ }
  };

  if (!update) return null;

  // A blocking screen, deliberately with no dismiss control.
  if (update.mandatory) {
    return (
      <div className="app-update-block" role="alertdialog" aria-modal="true" aria-labelledby="app-update-title">
        <div className="app-update-block-card">
          <span className="app-update-block-icon"><IconAlertTriangle size={28} /></span>
          <h2 className="app-update-block-title" id="app-update-title">Update required</h2>
          <p className="app-update-block-body">
            This version of AcyMart is too old to shop with safely. Install version {update.versionName} to continue.
          </p>
          <button type="button" className="app-update-btn" onClick={startDownload}>
            <IconDownload size={18} /> Download update
          </button>
          {update.notes && <p className="app-update-notes">{update.notes}</p>}
        </div>
      </div>
    );
  }

  // Snoozing is per-version, not per-time: dismissing 1.2 must not also hide
  // 1.3 when it lands next week.
  if (snoozedFor === String(update.versionCode)) return null;

  return (
    <div className="app-update-bar" role="status">
      <span className="app-update-bar-text">
        Version {update.versionName} is available
      </span>
      <button type="button" className="app-update-bar-cta" onClick={startDownload}>
        Update
      </button>
      <button type="button" className="app-update-bar-close" onClick={snooze} aria-label="Dismiss this update notice">
        <IconX size={16} />
      </button>
    </div>
  );
}
